import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { mapStockAdjustmentMeta } from '#/modules/imsMgmt/receipts/stockAdjustment/helper/mapStockAdjustmentMeta';

function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function AdjustmentHero({ meta, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold text-gray-900">{meta.code}</h1>
          {meta.status ? <DocumentStatusBadge status={meta.status} /> : null}
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

AdjustmentHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  actions: PropTypes.node,
};

function AdjustmentInfoGrid({ meta }) {
  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      <div>
        <p className="text-xs text-gray-500">建立</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.createDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">修改</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.reviseDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">完成</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.closeDate}</p>
      </div>
      <div className="hidden md:block" aria-hidden="true" />
      <div>
        <p className="text-xs text-gray-500">調整日</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.adjustmentDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">來源類型</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">
          {displayOrDash(meta.sourceTypeLabel)}
        </p>
      </div>
      <div>
        <p className="text-xs text-gray-500">項目數</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.lineCount}</p>
      </div>
      <div className="hidden md:block" aria-hidden="true" />
      <div className="sm:col-span-2 md:col-span-2">
        <p className="text-xs text-gray-500">開單原因</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.issueReason)}
        </p>
      </div>
      <div className="sm:col-span-2 md:col-span-2">
        <p className="text-xs text-gray-500">備註</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.remark)}
        </p>
      </div>
    </div>
  );
}

AdjustmentInfoGrid.propTypes = {
  meta: PropTypes.shape({
    createDate: PropTypes.string,
    reviseDate: PropTypes.string,
    closeDate: PropTypes.string,
    adjustmentDate: PropTypes.string,
    sourceTypeLabel: PropTypes.string,
    lineCount: PropTypes.number,
    issueReason: PropTypes.string,
    remark: PropTypes.string,
  }).isRequired,
};

const StockAdjustmentMainInfoCard = ({
  detail,
  embedded = false,
  actions = null,
  afterGrid = null,
}) => {
  const meta = mapStockAdjustmentMeta(detail);

  const content = (
    <>
      <AdjustmentHero meta={meta} actions={actions} />
      <AdjustmentInfoGrid meta={meta} />
      {afterGrid}
    </>
  );

  if (embedded) {
    return (
      <div className="border-b border-slate-200 p-5">
        {content}
      </div>
    );
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        {content}
      </div>
    </div>
  );
};

StockAdjustmentMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  embedded: PropTypes.bool,
  actions: PropTypes.node,
  afterGrid: PropTypes.node,
};

export default StockAdjustmentMainInfoCard;
